import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { cargarParametros } from "../utils/parametros";
import GestionarRetenciones from "../components/GestionarRetenciones";
import GestionarApariencia from "../components/GestionarApariencia";
import Cargando from "../components/Cargando";
import { Ayuda } from "../context/AyudaContext";

// Parámetros del sistema que el administrador cambia sin programar (IVA, plazo del crédito…).
// Cada cambio pide un motivo opcional y queda en el historial con quién y cuándo.
function ConfigurarParametros() {
  const [parametros, setParametros] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [editando, setEditando] = useState({}); // clave -> { valor, motivo }
  const [guardando, setGuardando] = useState("");
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  const cargar = async () => {
    setError("");
    try {
      const [lista, hist] = await Promise.all([apiFetch("/api/parametros"), apiFetch("/api/parametros/historial")]);
      setParametros(lista || []);
      setHistorial(hist || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const grupos = [...new Set(parametros.map((p) => p.grupo))];
  const fecha = (f) => new Date(f).toLocaleString("es-NI", { dateStyle: "short", timeStyle: "short" });

  const empezar = (p) => setEditando((prev) => ({ ...prev, [p.clave]: { valor: String(p.valor), motivo: "" } }));
  const cancelar = (clave) =>
    setEditando((prev) => {
      const { [clave]: _quitado, ...resto } = prev;
      return resto;
    });
  const cambiar = (clave, campo, valor) =>
    setEditando((prev) => ({ ...prev, [clave]: { ...prev[clave], [campo]: valor } }));

  const guardar = async (p) => {
    const edicion = editando[p.clave];
    setMensaje("");
    setError("");
    const numero = Number(edicion.valor);
    if (edicion.valor === "" || !Number.isFinite(numero)) return setError(`${p.etiqueta}: escribe un número válido.`);
    if (p.minimo !== null && numero < p.minimo) return setError(`${p.etiqueta}: el mínimo permitido es ${p.minimo}${p.unidad ? " " + p.unidad : ""}.`);
    if (p.maximo !== null && numero > p.maximo) return setError(`${p.etiqueta}: el máximo permitido es ${p.maximo}${p.unidad ? " " + p.unidad : ""}.`);
    setGuardando(p.clave);
    try {
      const r = await apiFetch(`/api/parametros/${p.clave}`, {
        method: "POST",
        body: JSON.stringify({ valor: numero, motivo: edicion.motivo }),
      });
      setMensaje(r?.message || "Parámetro actualizado.");
      cancelar(p.clave);
      await cargar();
      cargarParametros(); // para que las pantallas abiertas usen el valor nuevo
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando("");
    }
  };

  if (cargando) return <Cargando />;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ textAlign: "center", color: "var(--tema-titulo, #0b3c60)", marginBottom: 0 }}>🎚️ Parámetros del sistema</h2>
      <Ayuda>
      <p style={{ textAlign: "center", color: "#5b6472", fontSize: "0.9rem" }}>
        Valores que el sistema usa para calcular y validar. Los cambios aplican de inmediato y quedan en el historial.
      </p>
      </Ayuda>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}
      {mensaje && <p style={{ color: "#2f7a54", fontWeight: 600 }}>✓ {mensaje}</p>}

      {parametros.length === 0 && !error && <p className="vacio-simple">No hay parámetros todavía.</p>}

      {grupos.map((grupo) => (
        <div key={grupo} style={estilos.tarjeta}>
          <h3 style={{ margin: "0 0 0.8rem", color: "var(--tema-titulo, #0b3c60)" }}>{grupo}</h3>
          {parametros
            .filter((p) => p.grupo === grupo)
            .map((p) => {
              const edicion = editando[p.clave];
              return (
                <div key={p.clave} style={estilos.fila}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontWeight: 700, color: "var(--tema-titulo, #0b3c60)" }}>{p.etiqueta}</div>
                    <div style={{ fontSize: "0.8rem", color: "#5b6472", marginTop: "0.15rem" }}>{p.descripcion}</div>
                    <div style={{ fontSize: "0.72rem", color: "#8993a1", marginTop: "0.25rem" }}>
                      Permitido: {p.minimo ?? "—"} a {p.maximo ?? "—"} {p.unidad || ""}
                      {p.actualizado_en
                        ? ` · Último cambio: ${fecha(p.actualizado_en)}${p.actualizado_por_nombre ? " por " + p.actualizado_por_nombre : ""}`
                        : ""}
                    </div>
                  </div>
                  {!edicion ? (
                    <div style={estilos.valorBloque}>
                      <span style={estilos.valor}>
                        {p.valor}
                        {p.unidad ? <span style={estilos.unidad}> {p.unidad}</span> : null}
                      </span>
                      <button type="button" onClick={() => empezar(p)} style={estilos.botonSecundario}>
                        ✏️ Cambiar
                      </button>
                    </div>
                  ) : (
                    <div style={estilos.edicion}>
                      <label style={estilos.campo}>
                        Nuevo valor {p.unidad ? `(${p.unidad})` : ""}
                        <input
                          type="number"
                          step="any"
                          value={edicion.valor}
                          onChange={(e) => cambiar(p.clave, "valor", e.target.value)}
                          style={estilos.input}
                          autoFocus
                        />
                      </label>
                      <label style={estilos.campo}>
                        Motivo (opcional)
                        <input
                          type="text"
                          value={edicion.motivo}
                          onChange={(e) => cambiar(p.clave, "motivo", e.target.value)}
                          placeholder="Ej. cambio de ley, política nueva…"
                          style={{ ...estilos.input, minWidth: 200 }}
                        />
                      </label>
                      <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button type="button" disabled={guardando === p.clave} onClick={() => guardar(p)} style={estilos.botonPrincipal}>
                          {guardando === p.clave ? "Guardando..." : "💾 Guardar"}
                        </button>
                        <button type="button" onClick={() => cancelar(p.clave)} style={estilos.botonSecundario}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ))}

      <GestionarRetenciones />

      <GestionarApariencia />

      <div style={estilos.tarjeta}>
        <h3 style={{ margin: "0 0 0.8rem", color: "var(--tema-titulo, #0b3c60)" }}>🕘 Historial de cambios</h3>
        {historial.length === 0 ? (
          <p style={{ color: "#8993a1", fontSize: "0.9rem" }}>Todavía no se ha cambiado ningún parámetro.</p>
        ) : (
          <div className="table-scroll">
            <table style={estilos.tabla}>
              <thead>
                <tr>
                  <th style={estilos.th}>Fecha</th>
                  <th style={estilos.th}>Parámetro</th>
                  <th style={estilos.th}>Antes</th>
                  <th style={estilos.th}>Ahora</th>
                  <th style={estilos.th}>Quién</th>
                  <th style={estilos.th}>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h) => (
                  <tr key={h.id} style={{ borderBottom: "1px solid #eef1f3" }}>
                    <td style={estilos.td}>{fecha(h.cambiado_en)}</td>
                    <td style={estilos.td}>{h.etiqueta || h.clave}</td>
                    <td style={estilos.td}>
                      {h.valor_anterior ?? "—"} {h.unidad || ""}
                    </td>
                    <td style={{ ...estilos.td, fontWeight: 700 }}>
                      {h.valor_nuevo} {h.unidad || ""}
                    </td>
                    <td style={estilos.td}>{h.cambiado_por_nombre || "—"}</td>
                    <td style={estilos.td}>{h.motivo || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const estilos = {
  tarjeta: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 14, padding: "1.1rem 1.2rem", marginBottom: "1.2rem", boxShadow: "0 1px 3px rgba(27,42,74,0.06)" },
  fila: { display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between", padding: "0.8rem 0", borderTop: "1px solid #eef1f3" },
  valorBloque: { display: "flex", alignItems: "center", gap: "0.8rem" },
  valor: { fontSize: "1.5rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums" },
  unidad: { fontSize: "0.8rem", fontWeight: 600, color: "#5b6472" },
  edicion: { display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "flex-end" },
  campo: { display: "flex", flexDirection: "column", gap: "0.2rem", fontSize: "0.78rem", color: "#5b6472", fontWeight: 600 },
  input: { padding: "0.45rem 0.6rem", border: "1px solid #c9d6ea", borderRadius: 8, fontSize: "0.95rem", width: 110 },
  botonPrincipal: { background: "#2f7a54", color: "#fff", border: "none", borderRadius: 8, padding: "0.5rem 0.9rem", fontWeight: 700, cursor: "pointer" },
  botonSecundario: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #c9d6ea", borderRadius: 8, padding: "0.45rem 0.8rem", fontWeight: 600, cursor: "pointer" },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: { textAlign: "left", padding: "0.5rem 0.6rem", background: "var(--tema-primario, #1f4e96)", color: "#fff", fontSize: "0.75rem" },
  td: { padding: "0.5rem 0.6rem", color: "#1b2a4a" },
};

export default ConfigurarParametros;
